import React, { useEffect, useState } from "react";
import { ScriptableContext } from "chart.js";
import CustomDatePicker from "../../../../Shared/custom-date-picker/customDatePicker";
import InputDropdown from "../../../../Shared/input-dropdown/inputDropdown";
import { callApi } from "../../../../api/fetch";
import moment from "moment";
import { durationOptions } from "../../../../constants/data";
import {
  ACCENTCOLOR,
  DISABLEDCOLORDARK,
  GRAPHBLUEBORDERCOLOR,
  GRAPHGREENBORDERCOLOR,
  GREYBACKGROUNDCOLOR,
  WHITE,
} from "../../../../Shared/colors";

// Styles
import Styles from "../dashboard.module.scss";
import BillableNonBillableChart from "./BillableNonBillableChart";
import { ensureArray } from "../../../../utils/dashboardMappers";
import { testIdProps } from "../../../../utils/testIdProps";

type Dataset = {
  label: string;
  data: number[];
  backgroundColor: (context: ScriptableContext<"line">) => CanvasGradient;
  borderColor: string;
  fill: boolean;
};

const modeTabs = [
  {
    title: "Production",
    id: "PRODUCTION",
  },
  {
    title: "Test",
    id: "TEST",
  },
];

function ProductsUsage({ productOptions, preSelectedDatePreference }: any) {
  const org_id = localStorage.getItem("organisation_id");
  // const user: any = localStorage.getItem("@user");
  // const x_user_id = JSON.parse(user)._id;
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDurationRange, setCustomDurationRange] = useState({
    from: {
      day: "",
      month: "",
      year: "",
    },
    to: {
      day: "",
      month: "",
      year: "",
    },
  });

  const [selectedDatePreference, setSelectedDatePreference] = useState(
    preSelectedDatePreference
  );
  const [selectedProduct, setSelectedProduct] = useState({
    label: "All Products",
    value: "",
  });

  const [startDate, setStartDate] = useState<any>(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<any>(moment().format("YYYY-MM-DD"));
  const [currentMode, setCurrentMode] = useState<string>(modeTabs[0].id);
  const [graphLabels, setGraphLabels] = useState<string[]>([]);
  const [graphDataSet, setGraphDataSet] = useState<Dataset[]>([]);

  const getNewGraphData = async () => {
    if (!org_id) return;
    if (
      !moment(startDate, "YYYY-MM-DD", true).isValid() ||
      !moment(endDate, "YYYY-MM-DD", true).isValid()
    ) {
      return;
    }
    try {
      const selected_product = selectedProduct.value ?? "";
      const graphRes = await callApi(
        `/organisations/${org_id}/analytics/transaction-stats?${
          selected_product && `product_arn=${encodeURIComponent(selected_product)}&`
        }start_date=${startDate}&end_date=${endDate}&env=${currentMode}`,
        {
          method: "GET",
        }
      );

      // callApi unwraps envelope.data; legacy action endpoints may return a
      // non-array (e.g. org object) until a dedicated usage API exists.
      const stats = ensureArray(graphRes);
      const labels = getFormattedLabels(stats);
      setGraphLabels(labels);
      const dataSet = getTransactionCounts(stats);
      setGraphDataSet([
        {
          label: "Billable",
          data: dataSet.billableCounts,
          backgroundColor: (context: ScriptableContext<"line">) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 200);
            gradient.addColorStop(0, "rgba(0, 117, 243, 0.9)");
            gradient.addColorStop(0.5, "rgba(0, 117, 243, 0.5)");
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
            return gradient;
          }, // Blue Gradient
          borderColor: GRAPHBLUEBORDERCOLOR, // Blue border
          fill: true,
        },
        {
          label: "Non-Billable",
          data: dataSet.nonBillableCounts,
          backgroundColor: (context: ScriptableContext<"line">) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 200);
            gradient.addColorStop(0, "rgba(15, 157, 88, 0.7)");
            gradient.addColorStop(0.5, "rgba(15, 157, 88, 0.5)");
            gradient.addColorStop(1, "rgba(255,255,255, 0)");
            return gradient; // Green Gradient
          },
          borderColor: GRAPHGREENBORDERCOLOR, // Green border
          fill: true,
        },
      ]);
    } catch (err) {
      console.log(err);
    }
  };

  function getTransactionCounts(data: any) {
    const billableCounts = data?.map((item: any) => item.billable_txn_count);
    const nonBillableCounts = data?.map((item: any) => item.failure_txn_count);

    return { billableCounts, nonBillableCounts };
  }

  function getFormattedLabels(data: any) {
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return data?.map((item: any) => {
      const dateObj = item?.date?.value
        ? new Date(item?.date?.value)
        : new Date(item?.date);
      const dayOfWeek = daysOfWeek[dateObj.getDay()];
      const month = dateObj.getMonth() + 1;
      const day = dateObj.getDate();
      return `${dayOfWeek} ${month}/${day}`;
    });
  }

  useEffect(() => {
    getNewGraphData();
  }, [selectedProduct, startDate, endDate, org_id, currentMode]);

  useEffect(() => {
    function getCurrentTransactions() {
      try {
        let start_date, end_date, prev_start_date, prev_end_date;
        switch (selectedDatePreference) {
          case "Last 7 days": {
            start_date = moment().subtract(7, "days").format("YYYY-MM-DD");
            end_date = moment().format("YYYY-MM-DD");
            prev_start_date = moment().subtract(14, "days").format("YYYY-MM-DD");
            prev_end_date = start_date;
            break;
          }
          case "Last 30 days": {
            start_date = moment().subtract(30, "days").format("YYYY-MM-DD");
            end_date = moment().format("YYYY-MM-DD");
            break;
          }
          case "Last 60 days": {
            start_date = moment().subtract(60, "days").format("YYYY-MM-DD");
            end_date = moment().format("YYYY-MM-DD");
            prev_start_date = moment().subtract(120, "days").format("YYYY-MM-DD");
            prev_end_date = start_date;
            break;
          }
          case "Last 120 days": {
            start_date = moment().subtract(120, "days").format("YYYY-MM-DD");
            end_date = moment().format("YYYY-MM-DD");
            prev_start_date = moment().subtract(240, "days").format("YYYY-MM-DD");
            prev_end_date = start_date;
            break;
          }
          case "Custom": {
            if (!customDurationRange.from.day && !customDurationRange.to.day)
              return;

            start_date = `${customDurationRange.from.year}-${customDurationRange.from.month}-${customDurationRange.from.day}`;
            end_date = `${customDurationRange.to.year}-${customDurationRange.to.month}-${customDurationRange.to.day}`;
            const diff = moment(end_date, "YYYY-MM-DD").diff(
              moment(start_date, "YYYY-MM-DD"),
              "days"
            );
            prev_start_date = moment(
              moment(start_date, "YYYY-MM-DD").subtract(diff, "days")
            ).format("YYYY-MM-DD");
            prev_end_date = moment(
              moment(end_date, "YYYY-MM-DD").subtract(diff, "days")
            ).format("YYYY-MM-DD");
            break;
          }
        }

        setStartDate(moment(start_date).format("YYYY-MM-DD"));
        setEndDate(moment(end_date).format("YYYY-MM-DD"));
      } catch (error) {
        console.log(error);
      }
    }

    try {
      if (selectedDatePreference === "Custom" && showCustomDatePicker) return;
      getCurrentTransactions();
    } catch (error) {
      console.log(error);
    }
  }, [selectedDatePreference, customDurationRange]);

  const handleToggle = (mode: string) => {
    setCurrentMode(mode);
  };

  return (
    <main>
      {showCustomDatePicker && (
        <CustomDatePicker
          setShowCustomDatePicker={setShowCustomDatePicker}
          maxDateDifference={120}
          setCustomDurationRange={(data: any) => {
            setCustomDurationRange(data);
            setShowCustomDatePicker(false);
          }}
        />
      )}
      <div className={`${Styles.card} d-flex flex-column pt-3 pl-4 pr-4 mb-2`}>
        <div className="d-flex flex-column mr-5">
          <div className="d-flex align-items-center">
            <p className={Styles.cardHeader}>Product Usage</p>
          </div>
          <div
            style={{
              gap: "16px",
              width: "100%",
              display: "flex",
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div className="d-flex flex-column" style={{ flex: "0 0 auto" }}>
              <div
                style={{
                  marginBottom: "8px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "18px",
                  color: "black",
                  fontFamily: "Outfit",
                }}
              >
                Duration
              </div>
              <InputDropdown
                id="dashboard-product-usage-date"
                style={{ width: "160px" }}
                no_shadow="true"
                forDashboard={true}
                placeholder={preSelectedDatePreference}
                optionsArray={durationOptions}
                click={(value: any) => {
                  setSelectedDatePreference(value);
                  if (value === "Custom") {
                    setShowCustomDatePicker(true);
                  }
                }}
                resetSelection={() => {}}
              />
            </div>
            <div
              className="d-flex flex-column"
              style={{ flex: "1 1 200px", minWidth: "160px", maxWidth: "280px" }}
            >
              <div
                style={{
                  marginBottom: "8px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "18px",
                  color: "black",
                  fontFamily: "Outfit",
                }}
              >
                All Products
              </div>
              <InputDropdown
                id="product-usage-all-products"
                forDashboard={true}
                style={{ width: "100%", minWidth: 0 }}
                no_shadow="true"
                optionsArray={productOptions}
                placeholder={"All Products"}
                borderRadius="true"
                click={(value: any) => {
                  if (value && typeof value === "object") {
                    setSelectedProduct({
                      label: value.label ?? "All Products",
                      value: value.value ?? "",
                    });
                    return;
                  }
                  const match = (productOptions || []).find(
                    (option: { label?: string }) => option.label === value
                  );
                  setSelectedProduct(
                    match ?? { label: "All Products", value: "" }
                  );
                }}
              />
            </div>

            <div
              className="d-flex flex-row mr-3"
              style={{
                flex: "0 0 auto",
                height: "45px",
                backgroundColor: GREYBACKGROUNDCOLOR,
                borderRadius: 10,
                padding: 4,
                display: "flex",
                flexDirection: "row",
              }}
            >
              {modeTabs.map((tab) => {
                const isSelected = tab.id == currentMode;
                const backgroundColor = isSelected
                  ? WHITE
                  : GREYBACKGROUNDCOLOR;
                const textColor = isSelected ? ACCENTCOLOR : DISABLEDCOLORDARK;
                return (
                  <button
                    {...testIdProps(
                      `dashboard-product-usage-${tab.title.toLowerCase()}`
                    )}
                    key={tab.id}
                    onClick={() => {
                      handleToggle(tab.id);
                    }}
                    style={{
                      backgroundColor,
                      color: textColor,
                      width: 100,
                      fontSize: 14,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: 6,
                      outline: "none",
                      border: "none",
                    }}
                  >
                    {tab.title}
                  </button>
                );
              })}
            </div>
          </div>

          <BillableNonBillableChart
            graphLabels={graphLabels}
            graphDataSet={graphDataSet}
          />
        </div>
      </div>
    </main>
  );
}

export default ProductsUsage;
